import { ActionChip, BREAKPOINTS, ComponentL, ComponentTextStyle, HyperLink, LoadingIndicator, Paginator, SearchBar, Size, SystemIcons, TextButton } from "@laerdal/life-react-components";
import React, { useEffect } from "react";
import { useState } from "react";
import { useLocation } from "react-router";
import { KnowledgeArticleListItemDto } from "../../model/dto/knowledgeArticleListItemDto";
import { FacetItemDto, ProductLineFacetItemDto } from "../../model/dto/articleSearchWithFiltersResponseDto";
import SearchApi from "../../services/api/SearchApi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import posthog from 'posthog-js';
import CantFindCreateCase from "../../commonComponents/CantFindCreateCase";
import { useMediaMatch } from "rooks";
import { useTranslation } from "react-i18next";
import ArticleSearchFilters from "./components/ArticleSearchFilters";
import ArticleSearchNoResults from "./components/ArticleSearchNoResults";
import { marketList } from "../../model/constants/marketContants";
import { useSelector } from "react-redux";
import { selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { selectIsAuthenticated } from "../../store/slices/userSlice";
import TranslateWithAI from "../../commonComponents/TranslateWithAI";
import {
  ContentContainer, HeaderContainer, Search, TopFrame, SearchContainer,
  PaginatorContainer, ArticleContainers, ArticlePage,
  Article, ShowingLabel, MobileShowingLabel, ArticleDescription, ArticleMeta,
  Breadcrumb, CrumbLink, CrumbText, Devider, ResultsLayout,
  DesktopFilterSidebar, ResultsContent, MobileFilterChip, FilterOverlay,
  FilterOverlayContent, FilterOverlayFooter, FilterOverlayHeader,
  DesktopFilterHeader, DesktopFilterLabel,
  CloseButton, StyledInputChip,
} from "./ArticleSearchPage.styles";


function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}


const ArticleSearchPage = () => {
  let query = useQuery();

  const navigate = useNavigate();
  const { t, i18n } = useTranslation("articles");

  const initialArticleFilters = query.get("articleFilters")?.split(',').filter(Boolean) ?? [];
  const initialProductLines = query.get("productLines")?.split(',').filter(Boolean) ?? [];
  const initialPage = parseInt(query.get("page") ?? '1', 10) || 1;

  const [searchTerm, setSearchTerm] = useState<string>(query.get("query") ?? '');

  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [articlesLoading, setArticlesLoading] = useState<boolean>(true);
  const [articles, setArticles] = useState<KnowledgeArticleListItemDto[]>([]);

  const [showingFirstIndex, setShowingFirstIndex] = useState<number>(1);
  const [showingLastIndex, setShowingLastIndex] = useState<number>(1);
  const [showingTotlCount, setShowingTotlCount] = useState<number>(1);

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageCount, setPageCount] = useState<number>(1);

  const [abortController, setAbortController] = useState(new AbortController());
  const [selectedArticleFilters, setSelectedArticleFilters] = useState<string[]>(initialArticleFilters);
  const [selectedProductLineCodes, setSelectedProductLineCodes] = useState<string[]>(initialProductLines);
  const [articleFilterFacets, setArticleFilterFacets] = useState<FacetItemDto[]>([]);
  const [productLineCodeFacets, setProductLineCodeFacets] = useState<ProductLineFacetItemDto[]>([]);
  const [showFilterOverlay, setShowFilterOverlay] = useState<boolean>(false);
  const currentMartket = useSelector(selectMarket);
  const translateWithAi = useSelector(selectTranslateWithAi)
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const isMediumScreen = useMediaMatch(BREAKPOINTS.LARGE.replace('@media ', ''));

  useEffect(() => {
    const initialQuery = query.get("query");
    if (initialQuery) {
      executeSearch(initialQuery, initialPage, initialArticleFilters, initialProductLines);
    } else {
      setArticlesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTerm) {
      executeSearch(searchTerm, currentPage, selectedArticleFilters, selectedProductLineCodes, false);
    }
  }, [translateWithAi]);

  const articleCountPerPage = 12;

  const executeSearch = (term: string, page: number = 1, articleFilters: string[] = selectedArticleFilters, productLineFilters: string[] = selectedProductLineCodes, scrollToTop: boolean = true) => {


    const params = new URLSearchParams();
    if (term) params.set('query', term);
    if (articleFilters.length > 0) params.set('articleFilters', articleFilters.join(','));
    if (productLineFilters.length > 0) params.set('productLines', productLineFilters.join(','));
    if (page > 1) params.set('page', String(page));
    const queryString = params.toString();
    navigate(`/articles${queryString ? `?${queryString}` : ''}`);

    setShowPagination(false);
    setPage(term, page, articleFilters, productLineFilters, scrollToTop);
  };

  const toggleArticleFilter = (value: string) => {
    const wasSelected = selectedArticleFilters.includes(value);
    const newFilters = wasSelected
      ? selectedArticleFilters.filter(v => v !== value)
      : [...selectedArticleFilters, value];
    const facet = articleFilterFacets.find(f => f.value === value);
    posthog.capture('SUPP-ArticleSearchPage-ArticleTypeFilterChanged', {
      articleType: facet?.label ?? value,
      selected: !wasSelected,
      searchTerm: searchTerm,
    });
    setSelectedArticleFilters(newFilters);
    executeSearch(searchTerm, 1, newFilters, selectedProductLineCodes, false);
  };

  const toggleProductLineFilter = (code: string) => {
    const wasSelected = selectedProductLineCodes.includes(code);
    const newCodes = wasSelected
      ? selectedProductLineCodes.filter(c => c !== code)
      : [...selectedProductLineCodes, code];
    const facet = productLineCodeFacets.find(f => f.value === code);
    posthog.capture('SUPP-ArticleSearchPage-ProductFilterChanged', {
      productLineCode: code,
      productLineName: facet?.label ?? code,
      selected: !wasSelected,
      searchTerm: searchTerm,
    });
    setSelectedProductLineCodes(newCodes);
    executeSearch(searchTerm, 1, selectedArticleFilters, newCodes, false);
  };

  const toggleCategory = (categoryName: string, facets: ProductLineFacetItemDto[]) => {
    const codes = facets.map(f => f.value);
    const allSelected = codes.every(c => selectedProductLineCodes.includes(c));
    posthog.capture('SUPP-ArticleSearchPage-CategoryFilterChanged', {
      categoryName: categoryName,
      selected: !allSelected,
      searchTerm: searchTerm,
    });
    const newCodes = allSelected
      ? selectedProductLineCodes.filter(c => !codes.includes(c))
      : [...selectedProductLineCodes, ...codes.filter(c => !selectedProductLineCodes.includes(c))];
    setSelectedProductLineCodes(newCodes);
    executeSearch(searchTerm, 1, selectedArticleFilters, newCodes, false);
  };

  const hasFilters = articleFilterFacets.length > 0 || productLineCodeFacets.length > 0;
  const selectedFilterCount = selectedArticleFilters.length + selectedProductLineCodes.length;

  const resetAllFilters = () => {
    posthog.capture('SUPP-ArticleSearchPage-FiltersReset', { searchTerm: searchTerm });
    setSelectedArticleFilters([]);
    setSelectedProductLineCodes([]);
    executeSearch(searchTerm, 1, [], [], false);
  };

  const renderFilterContent = () => (
    <ArticleSearchFilters
      articleFilterFacets={articleFilterFacets}
      productLineCodeFacets={productLineCodeFacets}
      selectedArticleFilters={selectedArticleFilters}
      selectedProductLineCodes={selectedProductLineCodes}
      onToggleArticleFilter={toggleArticleFilter}
      onToggleProductLineFilter={toggleProductLineFilter}
      onToggleCategory={toggleCategory}
    />
  );

  const setPage = (term: string, page: number, articleFilters: string[] = selectedArticleFilters, productLineFilters: string[] = selectedProductLineCodes, scrollToTop: boolean = true) => {
    if (scrollToTop) {
      // @ts-ignore
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    setArticles([])
    setArticlesLoading(true);

    abortController.abort();
    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    const arrayIndex = page - 1;

    SearchApi.ArticleSearchWithFilters(
        term,
        arrayIndex,
        articleCountPerPage,
        productLineFilters.length > 0 ? productLineFilters : undefined,
        articleFilters.length > 0 ? articleFilters : undefined,
        translateWithAi ? marketList[currentMartket].locale : undefined,
        newAbortController.signal,
        undefined,
        true // light: list fields only
      )
      .then((articlesData) => {
        if (term) {
          // @ts-ignore
          posthog.capture('ArticleSearchPage Search', {
            searchTerm: term,
            isAuthenticated: isAuthenticated,
            paginationPage: arrayIndex+1
          });

          posthog.capture('SUPP-AllSearch', {
            searchTerm: term,
            page: 'ArticleSearchPage',
            isAuthenticated: isAuthenticated
          });
        }
    
        // Map the response to the expected format
        const mappedArticles = articlesData.items.map(article => ({
          articleNumber: article.articleNumber,
          title: article.title,
          shortPublicKnowledge: article.contentPlainText,
          description: article.contentPlainText,
          readTime: article.estimatedReadTimeMinutes ?? 0,
          viewCount: article.viewCount ?? 0,
          updatedAt: article.updatedAt,
        }));

        setArticles(mappedArticles);
        setCurrentPage(page);
        setPageCount(Math.floor(articlesData.totalCount / articleCountPerPage) + 1);


        setShowingFirstIndex((page - 1) * articleCountPerPage + 1);
        setShowingLastIndex(articlesData.totalCount < page * articleCountPerPage ? articlesData.totalCount : page * articleCountPerPage);
        setShowingTotlCount(articlesData.totalCount);

        setArticleFilterFacets(articlesData.articleFilterFacets ?? []);
        setProductLineCodeFacets(articlesData.productLineCodeFacets ?? []);

        setShowPagination(true)

        if (articlesData.items.length == 0) {
          // @ts-ignore
          posthog.capture('ArticleSearchPage SearchNoResults', {
            searchTerm: searchTerm,
            page: page
          });
        }
        setArticlesLoading(false)
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          setArticlesLoading(false)
        }
      })
  }

  return (
    <>

      
      <ContentContainer>
          <Breadcrumb>
            <CrumbLink href='/' onClick={(e) => {
              e.preventDefault();
              posthog.capture('SUPP-BreadcrumbClick', { currentUrl: window.location.href, clickedLink: currentMartket ? `/${currentMartket}` : '/', step: 1 });
              navigate(currentMartket ? `/${currentMartket}` : '/');
            }}>
              {t('Home')}
            </CrumbLink>
            <Devider>/</Devider>
            <CrumbText>
              {t('Search results')}
            </CrumbText>
          </Breadcrumb>
      </ContentContainer>
      <HeaderContainer>
        <ContentContainer>
          <TopFrame>
            <Search>
              <SearchBar
                id="DemoSearchBarSmall"
                placeholder={t('Type a question or search by keyword')}
                searchTerm={searchTerm}
                enterSearch={() => {
                  setSelectedArticleFilters([]);
                  setSelectedProductLineCodes([]);
                  executeSearch(searchTerm, 1, [], []);
                }}
                setSearchTerm={(a) => { setSearchTerm(a) }}
                disabled={articlesLoading}
                removeSearch={() => {
                  posthog.capture('SUPP-ArticleSearchPage-SearchCleared', {
                    searchTerm: searchTerm,
                  });
                  setSearchTerm('');
                  //@ts-ignore
                  document.getElementById('DemoSearchBarSmall').focus()
                }}
                size={Size.Small}
              />
            </Search>
          </TopFrame>
        </ContentContainer>
      </HeaderContainer>
      <ContentContainer>
      <ArticlePage>
        <ResultsLayout>
          {hasFilters && (
            <DesktopFilterSidebar>
              <DesktopFilterHeader>
                <DesktopFilterLabel>{t('Filters')}</DesktopFilterLabel>
                {selectedFilterCount > 0 && (
                  <TextButton icon={<SystemIcons.Loop />} variant="tertiary" size={Size.Small} invertFocus={false} onClick={resetAllFilters}>
                    {t('Reset all')}
                  </TextButton>
                )}
              </DesktopFilterHeader>
              {renderFilterContent()}
            </DesktopFilterSidebar>
          )}
          <ResultsContent>
          <SearchContainer>
            {hasFilters && (
              <>
                {!articlesLoading &&
                  <MobileShowingLabel>{t('Showing {{count}} of {{showingTotlCount}} results', { count: articles.length, showingTotlCount })}</MobileShowingLabel>
                }
                <MobileFilterChip>
                  <ActionChip icon={<SystemIcons.Filter />} size={Size.Small} text={t('Filter')} onClick={() => {
                    posthog.capture('SUPP-ArticleSearchPage-FilterOpened', { searchTerm: searchTerm });
                    setShowFilterOverlay(true);
                  }} />
                  {selectedArticleFilters.map(value => {
                    const facet = articleFilterFacets.find(f => f.value === value);
                    return facet ? (
                      <StyledInputChip key={value} size={Size.Small} text={`${facet.label} (${facet.count})`} onRemove={() => toggleArticleFilter(value)} />
                    ) : null;
                  })}
                  {selectedProductLineCodes.map(code => {
                    const facet = productLineCodeFacets.find(f => f.value === code);
                    return facet ? (
                      <StyledInputChip key={code} size={Size.Small} text={`${facet.label} (${facet.count})`} onRemove={() => toggleProductLineFilter(code)} />
                    ) : null;
                  })}
                </MobileFilterChip>
              </>
            )}
            {articlesLoading && <LoadingIndicator />}

            {!articlesLoading &&
              <ArticleContainers key="newToSimCpature">
                {marketList[currentMartket].locale != 'en' && <TranslateWithAI />}
                <ShowingLabel>{t('Showing {{count}} of {{showingTotlCount}} results', { count: articles.length, showingTotlCount })}</ShowingLabel>

                {
                  articles.map((item, index) =>
                  (
                    <Article>
                      <HyperLink
                        variant="default"
                        href={`/articles/${item.articleNumber}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // @ts-ignore
                          posthog.capture(' ', {
                            searchTerm: searchTerm,
                            relatedArticle: item.articleNumber,
                            rank: index,
                            page: currentPage
                          });
                          navigate(`/articles/${item.articleNumber}`)
                        }}>{item.title}</HyperLink>
                      <ArticleDescription>
                        {item.description.length > 350
                          ? `${item.description.substring(0, 350)}...`
                          : item.description}
                      </ArticleDescription>
                      <ArticleMeta>
                        <span>{item.readTime} {t('min read')}</span>
                        {item.updatedAt && <><SystemIcons.Bullet /><span>{new Date(item.updatedAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}</span></>}
                        <><SystemIcons.Bullet /><span>{item.viewCount} {t('views')}</span></>
                      </ArticleMeta>
                    </Article>
                  ))
                }
              </ArticleContainers>
            }

            {!articlesLoading && articles.length == 0 &&
              <ArticleSearchNoResults searchTerm={searchTerm} />
            }
            {/* TODO: test pagination */}
            {showPagination && articles.length != 0 &&
              <PaginatorContainer>
                <Paginator
                  pageCount={pageCount}
                  currentPage={currentPage}
                  onPageChange={(p) => {
                    // @ts-ignore
                    posthog.capture('ArticleSearchPage PaginationChange', {
                      currentPage: currentPage,
                      newPage: p
                    });
                    executeSearch(searchTerm, p, selectedArticleFilters, selectedProductLineCodes)
                    return true;
                  }}
                />
              </PaginatorContainer>
            }
          </SearchContainer>

          <CantFindCreateCase />
          </ResultsContent>
        </ResultsLayout>
      </ArticlePage>
      </ContentContainer>

      {showFilterOverlay && (
        <FilterOverlay>
          <FilterOverlayHeader>
            <ComponentL textStyle={ComponentTextStyle.Bold}>{t('Filters')}</ComponentL>
            <CloseButton onClick={() => {
              posthog.capture('SUPP-ArticleSearchPage-FilterClosed', { searchTerm: searchTerm });
              setShowFilterOverlay(false);
            }}>
              <SystemIcons.Close />
            </CloseButton>
          </FilterOverlayHeader>
          <FilterOverlayContent>
            {renderFilterContent()}
          </FilterOverlayContent>
          <FilterOverlayFooter>
            <TextButton icon={<SystemIcons.Loop />} variant="tertiary" size={Size.Medium} tabIndex={0} invertFocus={false} onClick={resetAllFilters}>
              {t('Reset all')}
            </TextButton>
            <TextButton variant="primary" size={Size.Medium} onClick={() => {
              posthog.capture('SUPP-ArticleSearchPage-FilterClosed', { searchTerm: searchTerm });
              setShowFilterOverlay(false);
            }}>
              {t('View results')} ({selectedFilterCount})
            </TextButton>
          </FilterOverlayFooter>
        </FilterOverlay>
      )}
    </>);
};

export default ArticleSearchPage;